import dayjs, { type Dayjs } from 'dayjs';
import { useCallback, useEffect, useEffectEvent, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@mui/material';
import { Input, useRbSystemAlert, CustomIndefiniteDatePicker, RBButton } from '@scmlatam/web-ui';
import { employeeService } from '../services/service';
import type {
    IUseFormAssignHiringProps,
    IHiringRow,
    Row,
    IAssignPositionQueryData,
    IEmployeeOrgpath,
    AssignHiringOrgpathModel,
    EditHiringOrgpathItemModel,
    OrgpathValue,
    PositionNodeValue,
    OrgpathResponse,
    AreaApiNode,
} from '../types/hooks';
import type { IHiringRange } from '../types/services';
import type { TimelineSegment, TimelineBoundaryChangeArgs, VirtualTreeNode, VirtualTreeNodeId, VirtualTreeSelectionChangeDetails } from '@scmlatam/web-ui';
import { usePermisoEmpleado as usePermiso } from '../../../../auth/permisosModulos';

const findPositionNode = (nodes: VirtualTreeNode<PositionNodeValue>[], row: IHiringRow): VirtualTreeNode<PositionNodeValue> | null => {
    let positionIdMatch: VirtualTreeNode<PositionNodeValue> | null = null;
    let positionNameMatch: VirtualTreeNode<PositionNodeValue> | null = null;

    for (const node of nodes) {
        if (node.type === 'work_position' && node.value) {
            const value = node.value;
            const samePositionId = row.work_position_id != null && value.work_position_id === row.work_position_id;
            const samePositionName = row.position.trim() !== '' && value.name.trim() === row.position.trim();
            if (samePositionId || samePositionName) {
                const sameCompany = !row.orgpath || value.company_id === row.orgpath.company_id;
                const sameAreas = !row.orgpath || value.area_ids.join('.') === row.orgpath.area_ids.join('.');
                if (sameCompany && sameAreas) return node;
                if (samePositionId) positionIdMatch ??= node;
                if (samePositionName) positionNameMatch ??= node;
            }
        }

        const found = node.children ? findPositionNode([...node.children], row) : null;
        if (found) return found;
    }

    return positionIdMatch ?? positionNameMatch;
};

// /area responde una jerarquía recursiva con los puestos embebidos.

const INDEFINITE_DATE: Dayjs = dayjs('3000-01-01');

const toDayjs = (value: Dayjs | string | null | undefined): Dayjs => {
    if (!value) {
        return dayjs();
    }
    if (dayjs.isDayjs(value)) {
        return value;
    }
    return dayjs(value);
};

const EMPTY_DATA: IAssignPositionQueryData = {
    hiring_range: [],
    subcompanies: [],
    areas: [],
    work_positions: [],
    orgpaths: [],
};

const fromUiDayjs = (value: { valueOf: () => number }): Dayjs => dayjs(value.valueOf());

// Los endpoints pueden responder el arreglo directo o envuelto en { data: [...] }.
const toArray = <T,>(result: PromiseSettledResult<unknown>): T[] => {
    if (result.status !== 'fulfilled') return [];
    const value = result.value;
    if (Array.isArray(value)) return value as T[];
    const nested = (value as { data?: unknown } | null)?.data;
    return Array.isArray(nested) ? (nested as T[]) : [];
};

const toOrgpathArray = (result: PromiseSettledResult<unknown>): IEmployeeOrgpath[] => {
    if (result.status !== 'fulfilled') return [];
    const value = result.value as { data?: unknown; orgpath?: unknown } | unknown;
    if (Array.isArray(value)) return value as IEmployeeOrgpath[];
    if (Array.isArray((value as { orgpath?: unknown })?.orgpath)) return (value as { orgpath: IEmployeeOrgpath[] }).orgpath;

    const nested = (value as { data?: unknown })?.data;
    if (Array.isArray(nested)) return nested as IEmployeeOrgpath[];
    if (Array.isArray((nested as { orgpath?: unknown } | null)?.orgpath)) return (nested as { orgpath: IEmployeeOrgpath[] }).orgpath;
    return [];
};

const normalizeHiringOrgpaths = (items: IEmployeeOrgpath[]): IEmployeeOrgpath[] =>
    items.flatMap((item) => {
        const response = item as OrgpathResponse;
        if (!response.orgpath?.length) return [item];

        return response.orgpath.map((orgpath) => ({
            ...orgpath,
            hiring_id: orgpath.hiring_id ?? item.hiring_id ?? item.Hiring?.hiring_id ?? item.hiring?.hiring_id,
            hiring_range_id: orgpath.hiring_range_id ?? item.hiring_range_id,
        }));
    });

const isIndefiniteDate = (value: Dayjs | string | null | undefined): boolean => toDayjs(value).format('YYYY-MM-DD') === '3000-01-01';

const isIndefinite = (row: IHiringRow): boolean => isIndefiniteDate(row.end_date);

const normalizeOrgpath = (orgpath: IEmployeeOrgpath | undefined): OrgpathValue | null => {
    if (!orgpath) return null;

    const response = orgpath as OrgpathResponse;
    const company = response.Subcompany ?? response.subcompany;
    const areas = response.Area ?? response.areas ?? [];
    const position = response.Posicion;
    const companyId = Number(company?.subcompany_id ?? company?.id);
    const areaIds = areas.map((area) => Number(area.area_id ?? area.id)).filter((areaId) => Number.isFinite(areaId));
    const workPositionId = Number(position?.work_position_id ?? position?.id);

    if (!Number.isFinite(companyId) || areaIds.length === 0 || !Number.isFinite(workPositionId)) return null;
    return { company_id: companyId, area_ids: areaIds, workposition_id: workPositionId };
};

const getOrgpathHiringId = (orgpath: IEmployeeOrgpath): number | null => {
    const hiringId = orgpath.hiring_id ?? orgpath.Hiring?.hiring_id ?? orgpath.hiring?.hiring_id;
    return hiringId == null ? null : Number(hiringId);
};

const getOrgpathPositionName = (orgpath: IEmployeeOrgpath | undefined): string => {
    if (!orgpath) return '';
    const response = orgpath as OrgpathResponse;
    return response.Posicion?.work_position_name ?? response.Posicion?.name ?? '';
};

type RowDateErrors = Record<number, string>;

const buildRowDateErrors = (
    rows: IHiringRow[],
    messages: {
        invalidDates: string;
        invalidRange: string;
        overlap: (row: IHiringRow) => string;
    },
): RowDateErrors => {
    const errors: Record<number, string[]> = {};
    const addError = (localId: number, message: string) => {
        errors[localId] = [...(errors[localId] ?? []), message];
    };

    rows.forEach((row) => {
        if (!row.start_date || !row.end_date) {
            addError(row.local_id, messages.invalidDates);
            return;
        }

        const start = toDayjs(row.start_date);
        const end = toDayjs(row.end_date);
        if (!start.isValid() || !end.isValid()) {
            addError(row.local_id, messages.invalidDates);
        } else if (!end.isAfter(start, 'day')) {
            addError(row.local_id, messages.invalidRange);
        }
    });

    for (let currentIndex = 0; currentIndex < rows.length; currentIndex += 1) {
        const current = rows[currentIndex];
        const currentStart = toDayjs(current.start_date);
        const currentEnd = toDayjs(current.end_date);
        if (!currentStart.isValid() || !currentEnd.isValid() || !currentEnd.isAfter(currentStart, 'day')) continue;

        for (let comparedIndex = currentIndex + 1; comparedIndex < rows.length; comparedIndex += 1) {
            const compared = rows[comparedIndex];
            const comparedStart = toDayjs(compared.start_date);
            const comparedEnd = toDayjs(compared.end_date);
            if (!comparedStart.isValid() || !comparedEnd.isValid() || !comparedEnd.isAfter(comparedStart, 'day')) continue;

            const overlaps = !currentEnd.isBefore(comparedStart, 'day') && !comparedEnd.isBefore(currentStart, 'day');
            if (overlaps) {
                addError(current.local_id, messages.overlap(compared));
                addError(compared.local_id, messages.overlap(current));
            }
        }
    }

    return Object.fromEntries(Object.entries(errors).map(([localId, rowErrors]) => [localId, rowErrors.join(' ')]));
};

const buildEmptyRow = (employeeId: number, localId: number, baseStartDate: Dayjs = dayjs()): IHiringRow => {
    const startDate = toDayjs(baseStartDate);
    return {
        id: `new-${localId}`,
        local_id: localId,
        hiring_range_id: null,
        hiring_id: null,
        employee_id: employeeId,
        start_date: startDate,
        end_date: startDate.add(30, 'day'),
        name_hiring: '',
        type_hiring: '',
        position: '',
        work_position_id: null,
        hiring_orpath_id: null,
        orgpath: null,
        originalOrgpath: null,
        enable: false,
        automatic_renewal: false,
    };
};

const validateRows = (rows: IHiringRow[], t: (key: string) => string): string | null => {
    if (rows.length === 0) {
        return t('assign_hiring.validation.empty');
    }

    for (const row of rows) {
        if (!(row.name_hiring || '').trim()) return t('assign_hiring.validation.name');
        if (!(row.type_hiring || '').trim()) return t('assign_hiring.validation.type');
        if (!(row.position || '').trim()) return t('assign_hiring.validation.position');
    }

    const sorted = [...rows].sort((a, b) => toDayjs(a.start_date).valueOf() - toDayjs(b.start_date).valueOf());
    const indefiniteRows = sorted.filter(isIndefinite);
    if (indefiniteRows.length > 1) {
        return t('assign_hiring.validation.max_indefinite');
    }
    if (indefiniteRows.length === 1 && !isIndefinite(sorted[sorted.length - 1])) {
        return t('assign_hiring.validation.indefinite_last');
    }

    return null;
};

export const useFormAssignHiring = ({ rows: tableRows, rowId, reload, option, setOption }: IUseFormAssignHiringProps) => {
    const { t } = useTranslation('workers.form.assign_hiring');
    const { showAlert } = useRbSystemAlert();
    const canAssignContract = usePermiso('asignar-contrato');

    const selectedRow: Row | null = useMemo(() => {
        if (!rowId) return null;
        return tableRows.find((r) => r.id === rowId) ?? null;
    }, [rowId, tableRows]);

    const employeeId = useMemo(() => (selectedRow ? Number(selectedRow.id) : 0), [selectedRow]);

    const employeeLabel = useMemo(() => {
        if (!selectedRow) return '';
        return `${selectedRow.person_number} - ${selectedRow.fullname}`;
    }, [selectedRow]);

    const [openForm, setOpenForm] = useState<boolean>(false);
    const [rows, setRows] = useState<IHiringRow[]>([]);
    const [deletedHiringIds, setDeletedHiringIds] = useState<number[]>([]);
    const [deletedHiringRangeIds, setDeletedHiringRangeIds] = useState<number[]>([]);
    const [deletedOrgpaths, setDeletedOrgpaths] = useState<AssignHiringOrgpathModel[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [saving, setSaving] = useState<boolean>(false);
    const [showMessage, setShowMessage] = useState<boolean>(false);
    const [editingRowId, setEditingRowId] = useState<number | null>(null);
    const [data, setData] = useState<IAssignPositionQueryData>(EMPTY_DATA);
    const [openTree, setOpenTree] = useState<boolean>(false);
    const [positionRowId, setPositionRowId] = useState<number | null>(null);
    const [selectedPosition, setSelectedPosition] = useState<PositionNodeValue | null>(null);

    const localIdCounter = useRef<number>(1);
    const hasUnsavedChangesRef = useRef<boolean>(false);
    const rowsRef = useRef<IHiringRow[]>(rows);
    rowsRef.current = rows;

    const sortedRows = useMemo(() => [...rows].sort((a, b) => toDayjs(a.start_date).valueOf() - toDayjs(b.start_date).valueOf()), [rows]);

    const editingRow = useMemo(() => sortedRows.find((row) => row.local_id === editingRowId) ?? null, [editingRowId, sortedRows]);

    const rowDateErrors = useMemo(
        () =>
            buildRowDateErrors(rows, {
                invalidDates: t('assign_hiring.validation.invalid_dates'),
                invalidRange: t('assign_hiring.validation.invalid_range'),
                overlap: (conflictingRow) =>
                    t('assign_hiring.validation.overlap_detail', {
                        name: conflictingRow.name_hiring.trim() || t('assign_hiring.timeline.untitled'),
                        start: toDayjs(conflictingRow.start_date).format('DD/MM/YYYY'),
                        end: isIndefinite(conflictingRow) ? t('assign_hiring.table.indefinite') : toDayjs(conflictingRow.end_date).format('DD/MM/YYYY'),
                    }),
            }),
        [rows, t],
    );

    const markDirty = useCallback(() => {
        hasUnsavedChangesRef.current = true;
    }, []);

    const hasChanges = useCallback(() => hasUnsavedChangesRef.current, []);

    const resetState = useCallback(() => {
        setRows([]);
        setDeletedHiringIds([]);
        setDeletedHiringRangeIds([]);
        setDeletedOrgpaths([]);
        setEditingRowId(null);
        setSaving(false);
        setLoading(false);
        setShowMessage(false);
        hasUnsavedChangesRef.current = false;
    }, []);

    const closeForm = useCallback(
        (skipConfirm = false) => {
            if (!skipConfirm && hasUnsavedChangesRef.current) {
                setShowMessage(true);
                return;
            }
            setOpenForm(false);
            setOption(null);
            resetState();
        },
        [resetState, setOption],
    );

    const loadData = useEffectEvent(async () => {
        setLoading(true);
        try {
            const [hiring_range, subcompanies, areas, work_positions, orgpaths] = await Promise.allSettled([
                employeeService.getHiringRanges({ employee_id: employeeId }),
                employeeService.getSubcompany(),
                employeeService.getArea(),
                employeeService.getWorkPosition(),
                employeeService.getHiringOrgpath({ employee_id: employeeId }),
                // utilizar el endpoint get all area para formar el árbol de areas, y no el endpoint get area que devuelve solo las areas asignadas al empleado
            ]);

            const hiringRanges = toArray<IHiringRange>(hiring_range);
            const apiOrgpaths = toOrgpathArray(orgpaths);
            const tableOrgpaths = selectedRow?.orgpaths ?? [];
            const seenOrgpathIds = new Set<number | string>();
            const mergedOrgpaths = [...tableOrgpaths, ...apiOrgpaths].filter((item) => {
                const orgpathId = item.hiring_orpath_id;
                if (orgpathId == null) return true;
                if (seenOrgpathIds.has(orgpathId)) return false;
                seenOrgpathIds.add(orgpathId);
                return true;
            });
            const hiringOrgpaths = normalizeHiringOrgpaths(mergedOrgpaths);

            setData({
                hiring_range: hiringRanges,
                subcompanies: toArray(subcompanies),
                areas: toArray(areas),
                work_positions: toArray(work_positions),
                orgpaths: hiringOrgpaths,
            });

            const mapped: IHiringRow[] = hiringRanges.map((range, index) => {
                const hiring = range.hiring;
                const existingOrgpath = hiringOrgpaths.find(
                    (item) =>
                        (getOrgpathHiringId(item) != null && getOrgpathHiringId(item) === Number(hiring?.hiring_id)) ||
                        (item.hiring_range_id != null && Number(item.hiring_range_id) === Number(range.hiring_range_id)),
                );
                const currentOrgpath = normalizeOrgpath(existingOrgpath);
                return {
                    id: `${employeeId}-${index}`,
                    local_id: index + 1,
                    hiring_range_id: range.hiring_range_id,
                    hiring_id: hiring?.hiring_id ?? null,
                    employee_id: range.employee_id,
                    start_date: toDayjs(range.start_date),
                    end_date: toDayjs(range.end_date),
                    name_hiring: hiring?.name_hiring ?? '',
                    type_hiring: hiring?.type_hiring ?? '',
                    position: getOrgpathPositionName(existingOrgpath) || hiring?.position || '',
                    work_position_id: currentOrgpath?.workposition_id ?? null,
                    hiring_orpath_id: existingOrgpath?.hiring_orpath_id ? Number(existingOrgpath.hiring_orpath_id) : null,
                    orgpath: currentOrgpath,
                    originalOrgpath: currentOrgpath,
                    enable: !!hiring?.enable,
                    automatic_renewal: !!hiring?.automatic_renewal,
                };
            });

            localIdCounter.current = mapped.length + 1;
            setRows(mapped);
            setDeletedHiringIds([]);
            setDeletedHiringRangeIds([]);
            setDeletedOrgpaths([]);
            hasUnsavedChangesRef.current = false;
        } catch (error) {
            console.error(error);
            showAlert('error', t('assign_hiring.alerts.load_error.message'));
        } finally {
            setLoading(false);
        }
    });

    useEffect(() => {
        if (option === 'assign_contract') {
            if (!canAssignContract) {
                setOption(null);
                return;
            }
            setOpenForm(true);
            loadData();
        }
    }, [employeeId, option, canAssignContract]);

    const updateRow = useCallback(
        (localId: number, patch: Partial<IHiringRow>) => {
            setRows((previous) => previous.map((row) => (row.local_id === localId ? { ...row, ...patch } : row)));
            markDirty();
        },
        [markDirty],
    );

    const addRow = useCallback(() => {
        const today = dayjs().startOf('day');
        const currentRows = [...rowsRef.current].sort((a, b) => toDayjs(a.start_date).valueOf() - toDayjs(b.start_date).valueOf());
        const indefiniteRows = currentRows.filter(isIndefinite);
        if (indefiniteRows.some((row) => !today.isAfter(toDayjs(row.start_date), 'day'))) {
            showAlert('error', t('assign_hiring.alerts.add_blocked.message'));
            return;
        }

        const lastRow = currentRows[currentRows.length - 1];
        const baseStart = indefiniteRows.length > 0 || !lastRow ? today : dayjs(Math.max(today.valueOf(), toDayjs(lastRow.end_date).add(1, 'day').valueOf()));
        const nextRow = { ...buildEmptyRow(employeeId, localIdCounter.current, baseStart), end_date: INDEFINITE_DATE };
        localIdCounter.current += 1;
        const nextRows = [...currentRows.map((row) => (isIndefinite(row) ? { ...row, end_date: today.subtract(1, 'day') } : row)), nextRow];
        rowsRef.current = nextRows;
        setRows(nextRows);
        markDirty();
    }, [employeeId, markDirty, showAlert, t]);

    const deleteRow = useCallback(
        (localId: number) => {
            setRows((previous) => {
                const target = previous.find((r) => r.local_id === localId);
                if (target?.hiring_id) setDeletedHiringIds((prev) => [...new Set([...prev, target.hiring_id as number])]);
                if (target?.hiring_range_id) setDeletedHiringRangeIds((prev) => [...new Set([...prev, target.hiring_range_id as number])]);
                const hiringId = target?.hiring_id;
                const hiringOrpathId = target?.hiring_orpath_id;
                const originalOrgpath = target?.originalOrgpath;
                if (hiringId != null && hiringOrpathId != null && originalOrgpath) {
                    setDeletedOrgpaths((prev) => [
                        ...prev,
                        {
                            employee_id: target.employee_id,
                            hiring_id: hiringId,
                            orgpath: [
                                {
                                    hiring_orpath_id: hiringOrpathId,
                                    to_delete: true,
                                    subcompany: originalOrgpath.company_id,
                                    areas: originalOrgpath.area_ids,
                                    puesto: originalOrgpath.workposition_id,
                                },
                            ],
                        },
                    ]);
                }
                return previous.filter((r) => r.local_id !== localId);
            });
            markDirty();
        },
        [markDirty],
    );

    const positionTreeItems = useMemo<VirtualTreeNode<PositionNodeValue>[]>(() => {
        const subcompanies = Array.isArray(data.subcompanies) ? data.subcompanies : [];
        const areas = (Array.isArray(data.areas) ? data.areas : []) as unknown as AreaApiNode[];

        // Un mismo puesto/área puede repetirse en varias ramas, así que el id se compone con la ruta.
        const buildAreaNode = (area: AreaApiNode, parentId: string, companyId: number, areaIds: number[] = []): VirtualTreeNode<PositionNodeValue> => {
            const areaId = `${parentId}/area-${area.area_id}`;
            const currentAreaIds = [...areaIds, area.area_id];
            return {
                id: areaId,
                label: area.area_name,
                type: 'area',
                children: [
                    ...(area.subareas ?? []).sort((a, b) => a.area_level - b.area_level).map((subarea) => buildAreaNode(subarea, areaId, companyId, currentAreaIds)),
                    ...(area.work_positions ?? []).map((workPosition) => {
                        const nodeId = `${areaId}/work-position-${workPosition.work_position_id}`;
                        return {
                            id: nodeId,
                            label: workPosition.work_position_name,
                            type: 'work_position' as const,
                            value: {
                                company_id: companyId,
                                area_ids: currentAreaIds,
                                work_position_id: workPosition.work_position_id,
                                name: workPosition.work_position_name,
                                nodeId,
                            },
                        };
                    }),
                ],
            };
        };

        return subcompanies.map((subcompany) => {
            const subcompanyId = `subcompany-${subcompany.subcompany_id}`;
            return {
                id: subcompanyId,
                label: subcompany.subcompany_name,
                type: 'subcompany',
                children: areas
                    .filter((area) => area.subcompany_id === subcompany.subcompany_id)
                    .sort((a, b) => a.area_level - b.area_level)
                    .map((area) => buildAreaNode(area, subcompanyId, subcompany.subcompany_id)),
            };
        });
    }, [data]);

    const openPositionTree = useCallback(
        (localId: number) => {
            const row = rows.find((item) => item.local_id === localId);
            const selectedNode = row ? findPositionNode(positionTreeItems, row) : null;
            setPositionRowId(localId);
            setSelectedPosition(selectedNode?.value ?? null);
            setOpenTree(true);
        },
        [positionTreeItems, rows],
    );

    const closePositionTree = useCallback(() => {
        setOpenTree(false);
        setPositionRowId(null);
        setSelectedPosition(null);
    }, []);

    const handlePositionSelectionChange = useCallback((_selectedIds: VirtualTreeNodeId[], details: VirtualTreeSelectionChangeDetails<PositionNodeValue>) => {
        const node = details.node;
        setSelectedPosition(node && node.type === 'work_position' && node.value ? node.value : null);
    }, []);

    const confirmPositionSelection = useCallback(() => {
        if (positionRowId === null || !selectedPosition) return;

        updateRow(positionRowId, {
            position: selectedPosition.name,
            work_position_id: selectedPosition.work_position_id,
            orgpath: {
                company_id: selectedPosition.company_id,
                area_ids: selectedPosition.area_ids,
                workposition_id: selectedPosition.work_position_id,
            },
        });
        closePositionTree();
    }, [closePositionTree, data, employeeId, positionRowId, selectedPosition, updateRow]);

    const openEditDetail = useCallback((localId: number) => {
        setEditingRowId(localId);
    }, []);

    const closeEditDetail = useCallback(() => {
        setEditingRowId(null);
    }, []);

    const columns = useMemo(
        () => [
            {
                key: 'delete',
                header: '',
                width: 'minmax(48px, 48px)',
                render: (row: IHiringRow) => (
                    <RBButton variant="ghost" className="hover:bg-transparent focus-visible:bg-transparent" disabled={loading || saving} onClick={() => deleteRow(row.local_id)} aria-label="delete">
                        <Icon baseClassName="material-symbols-rounded" sx={{ color: '#26000B', fontSize: '22px' }}>
                            do_not_disturb_on
                        </Icon>
                    </RBButton>
                ),
            },
            {
                key: 'name_hiring',
                header: <span className="block w-full text-left">{t('assign_hiring.fields.hiring_name')}</span>,
                width: 'minmax(80px, 1fr)',
                render: (row: IHiringRow) => (
                    <Input
                        type="text"
                        labelVariant="notched"
                        placeholder={t('assign_hiring.placeholder.hiring_name')}
                        value={row.name_hiring}
                        disabled={loading || saving || (row.hiring_range_id !== null && editingRowId !== row.local_id)}
                        onChange={(event) => updateRow(row.local_id, { name_hiring: event.target.value })}
                    />
                ),
            },
            {
                key: 'start_date',
                header: <span className="block w-full text-left">{t('assign_hiring.fields.start_date')}</span>,
                width: 'minmax(80px, 1fr)',
                truncate: false,
                render: (row: IHiringRow) => {
                    const start = toDayjs(row.start_date);
                    const helperText = rowDateErrors[row.local_id];

                    return (
                        <CustomIndefiniteDatePicker
                            label={''}
                            placeholder={t('assign_hiring.placeholder.date')}
                            value={start}
                            setValue={(value) => {
                                if (value?.isValid()) updateRow(row.local_id, { start_date: fromUiDayjs(value) });
                            }}
                            indefinite={false}
                            onIndefiniteChange={() => undefined}
                            indefiniteLabel={t('assign_hiring.table.indefinite')}
                            showIndefiniteCheckbox
                            error={!!helperText}
                            helperText={helperText}
                            disabled={loading || saving || (row.hiring_range_id !== null && editingRowId !== row.local_id)}
                            white
                            fullWidth
                            disabledIndefinite
                            disabledIndefiniteTooltip={t('assign_hiring.validation.indefinite_start_tooltip')}
                        />
                    );
                },
            },
            {
                key: 'end_date',
                header: <span className="block w-full text-left">{t('assign_hiring.fields.end_date')}</span>,
                width: 'minmax(80px, 1fr)',
                truncate: false,
                render: (row: IHiringRow) => {
                    const start = toDayjs(row.start_date);
                    const end = toDayjs(row.end_date);
                    const indefinite = isIndefinite(row);
                    const hasOtherIndefinite = sortedRows.some((item) => item.local_id !== row.local_id && isIndefinite(item));
                    const helperText = rowDateErrors[row.local_id];

                    return (
                        <CustomIndefiniteDatePicker
                            label={''}
                            value={end}
                            setValue={(value) => {
                                if (value?.isValid()) updateRow(row.local_id, { end_date: fromUiDayjs(value) });
                            }}
                            indefinite={indefinite}
                            onIndefiniteChange={(checked) => updateRow(row.local_id, { end_date: checked ? INDEFINITE_DATE : start.add(30, 'day') })}
                            indefiniteLabel={t('assign_hiring.table.indefinite')}
                            showIndefiniteCheckbox
                            disabledIndefinite={hasOtherIndefinite}
                            disabledIndefiniteTooltip={hasOtherIndefinite ? t('assign_hiring.validation.indefinite_exists_tooltip') : undefined}
                            error={!!helperText}
                            helperText={helperText}
                            disabled={loading || saving || (row.hiring_range_id !== null && editingRowId !== row.local_id)}
                            white
                            fullWidth
                            placeholder={t('assign_hiring.placeholder.date')}
                        />
                    );
                },
            },
            {
                key: 'type_hiring',
                header: <span className="block w-full text-left">{t('assign_hiring.fields.hiring_type')}</span>,
                width: 'minmax(80px, 1.2fr)',
                render: (row: IHiringRow) => (
                    <Input
                        type="text"
                        labelVariant="notched"
                        placeholder={t('assign_hiring.placeholder.hiring_type')}
                        value={row.type_hiring}
                        disabled={loading || saving || (row.hiring_range_id !== null && editingRowId !== row.local_id)}
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) => updateRow(row.local_id, { type_hiring: event.target.value })}
                    />
                ),
            },
            {
                key: 'position',
                header: <span className="block w-full text-left">{t('assign_hiring.fields.position')}</span>,
                width: 'minmax(80px, 1.2fr)',
                render: (row: IHiringRow) => (
                    <div className="flex min-w-0 flex-auto justify-left">
                        <RBButton
                            variant="secondaryPrimaryDark"
                            size="s"
                            className="min-w-0 max-w-full"
                            // title={row.position?.trim() || undefined}
                            disabled={loading || saving || (row.hiring_range_id !== null && editingRowId !== row.local_id)}
                            rightIcon={<Icon baseClassName="material-symbols-rounded">add_square</Icon>}
                            onClick={() => openPositionTree(row.local_id)}
                        >
                            <span className="block min-w-0 truncate">{row.position?.trim() || t('assign_hiring.placeholder.position')}</span>
                        </RBButton>
                    </div>
                ),
            },
            {
                key: 'edit',
                header: '',
                width: 'minmax(48px, 48px)',
                render: (row: IHiringRow) => (
                    <RBButton variant="ghost" className="hover:bg-transparent focus-visible:bg-transparent" disabled={loading || saving} onClick={() => openEditDetail(row.local_id)}>
                        <Icon baseClassName="material-symbols-rounded" sx={{ color: '#26000B', fontSize: '22px' }}>
                            edit_square
                        </Icon>
                    </RBButton>
                ),
            },
        ],
        [deleteRow, editingRowId, loading, openEditDetail, openPositionTree, rowDateErrors, saving, sortedRows, t, updateRow],
    );

    const validationMessage = useMemo(() => validateRows(rows, t), [rows, t]);
    const hasDateErrors = Object.keys(rowDateErrors).length > 0;
    const hasMissingRequiredFields = rows.some((row) => !row.name_hiring?.trim() || !row.type_hiring?.trim() || !row.position?.trim() || !row.start_date || !row.end_date);
    const hasActiveErrors = Boolean(validationMessage) || hasDateErrors;
    const canSubmit = rows.length > 0 && !hasMissingRequiredFields && !hasActiveErrors && !loading && !saving;

    const persistRows = useCallback(async () => {
        if (!canAssignContract) return;

        if (validationMessage || hasDateErrors) {
            if (!validationMessage) return;
            showAlert('error', validationMessage);
            return;
        }

        setSaving(true);
        try {
            const orderedRows = [...rows].sort((a, b) => toDayjs(a.start_date).valueOf() - toDayjs(b.start_date).valueOf());

            const orgpathUpdatesByHiringId = new Map<number, EditHiringOrgpathItemModel[]>();

            for (const deleteOrgpath of deletedOrgpaths) await employeeService.assignHiringOrgpath(deleteOrgpath);

            for (const hiringId of deletedHiringIds) await employeeService.deleteHiring(hiringId);
            for (const hiringRangeId of deletedHiringRangeIds) await employeeService.deleteHiringRange(hiringRangeId);

            for (const row of orderedRows) {
                const rangePayload = {
                    employee_id: row.employee_id,
                    start_date: toDayjs(row.start_date).format('YYYY-MM-DD'),
                    end_date: toDayjs(row.end_date).format('YYYY-MM-DD'),
                };

                let hiringRangeId: number | null = row.hiring_range_id;
                if (hiringRangeId) {
                    await employeeService.updateHiringRange(hiringRangeId, {
                        hiring_range_id: hiringRangeId,
                        ...rangePayload,
                    });
                } else {
                    const created = await employeeService.createHiringRange(rangePayload);
                    hiringRangeId = created?.data?.hiring_range_id ?? created?.hiring_range_id ?? null;
                }

                const hiringPayload = {
                    name_hiring: row.name_hiring.trim(),
                    type_hiring: (row.type_hiring || row.name_hiring).trim(),
                    enable: true,
                    position: row.position.trim(),
                    automatic_renewal: true,
                };

                let hiringId = row.hiring_id;
                if (hiringId) {
                    await employeeService.updateHiring(hiringId, hiringPayload);
                } else if (hiringRangeId) {
                    const createdHiring = await employeeService.createHiring({
                        hiring_range_id: hiringRangeId,
                        ...hiringPayload,
                    });
                    hiringId = createdHiring?.data?.hiring_id ?? createdHiring?.hiring_id ?? null;
                }

                if (hiringId == null) {
                    throw new Error(`No se pudo obtener el hiring_id para la fila ${row.local_id}`);
                }

                const isNewRow = row.hiring_range_id == null;

                if (!row.orgpath) {
                    if (isNewRow) throw new Error(`La fila ${row.local_id} no tiene un orgpath válido`);
                    continue;
                }
                const currentOrgpath = row.orgpath;

                if (currentOrgpath.area_ids.length === 0) {
                    if (isNewRow) throw new Error(`La fila ${row.local_id} no tiene áreas válidas`);
                    continue;
                }

                const orgpathValues = {
                    subcompany: currentOrgpath.company_id,
                    areas: currentOrgpath.area_ids,
                    puesto: currentOrgpath.workposition_id,
                    start_date: toDayjs(row.start_date).format('YYYY-MM-DD'),
                    end_date: toDayjs(row.end_date).format('YYYY-MM-DD'),
                };

                if (row.hiring_orpath_id != null) {
                    const contractOrgpaths = orgpathUpdatesByHiringId.get(hiringId) ?? [];
                    contractOrgpaths.push({ hiring_orpath_id: row.hiring_orpath_id, ...orgpathValues });
                    orgpathUpdatesByHiringId.set(hiringId, contractOrgpaths);
                    continue;
                }

                const orgpathResult = await employeeService.assignHiringOrgpath({
                    employee_id: row.employee_id,
                    hiring_id: hiringId,
                    orgpath: [{ hiring_orpath_id: null, to_delete: false, ...orgpathValues }],
                });
                const skippedAdjustment = orgpathResult.adjustments?.find((adjustment) => adjustment.field === 'skipped');
                if (skippedAdjustment) {
                    throw new Error(`El backend omitió el orgpath: ${skippedAdjustment.reason}`);
                }
            }

            if (orgpathUpdatesByHiringId.size > 0) {
                await employeeService.updateHiringOrgpath({
                    employee_id: employeeId,
                    contracts: [...orgpathUpdatesByHiringId].map(([hiring_id, orgpath]) => ({ hiring_id, orgpath })),
                });
            }

            showAlert('success', t('assign_hiring.alerts.assign_success.message'));
            await reload();
            hasUnsavedChangesRef.current = false;
            closeForm(true);
        } catch (error) {
            console.error(error);
            showAlert('error', t('assign_hiring.alerts.assign_error.message'));
        } finally {
            setSaving(false);
        }
    }, [canAssignContract, closeForm, deletedHiringIds, deletedHiringRangeIds, deletedOrgpaths, hasDateErrors, reload, rows, showAlert, t, validationMessage]);

    const timelineSegments = useMemo<TimelineSegment[]>(() => {
        return sortedRows
            .filter((row) => {
                const start = toDayjs(row.start_date);
                const end = toDayjs(row.end_date);
                return start.isValid() && end.isValid() && end.isAfter(start, 'day');
            })
            .map((row) => ({
                id: row.local_id,
                startDate: toDayjs(row.start_date).startOf('day'),
                endDate: toDayjs(row.end_date).startOf('day'),
                label: row.name_hiring.trim() || t('assign_hiring.timeline.untitled'),
            }));
    }, [sortedRows, t]);

    const formatTimelineDate = useCallback(
        (date: Dayjs) => {
            const localDate = fromUiDayjs(date);
            return localDate.isSame(INDEFINITE_DATE, 'day') ? t('assign_hiring.table.indefinite') : localDate.format('DD/MM/YYYY');
        },
        [t],
    );

    const handleBoundaryDateChange = useCallback(
        ({ kind, date, segmentIndex, minDate, maxDate }: TimelineBoundaryChangeArgs) => {
            const segment = timelineSegments[segmentIndex];
            if (!segment) return;

            const localDate = fromUiDayjs(date);
            const localMin = minDate ? fromUiDayjs(minDate) : null;
            const localMax = maxDate ? fromUiDayjs(maxDate) : null;
            if ((localMin && localDate.isBefore(localMin, 'day')) || (localMax && localDate.isAfter(localMax, 'day'))) return;

            if (kind === 'end') {
                if (fromUiDayjs(segment.endDate).isSame(INDEFINITE_DATE, 'day') || !localDate.isAfter(fromUiDayjs(segment.startDate), 'day')) return;
                updateRow(Number(segment.id), { end_date: localDate });
                return;
            }

            if (kind !== 'start' || !localDate.isBefore(fromUiDayjs(segment.endDate), 'day')) return;
            updateRow(Number(segment.id), { start_date: localDate });
        },
        [timelineSegments, updateRow],
    );

    return {
        t,
        title: t('assign_hiring.title'),
        openForm,
        setOpenForm,
        loading,
        saving,
        rows: sortedRows,
        columns,
        employeeLabel,
        showMessage,
        setShowMessage,
        hasChanges,
        closeForm,
        addRow,
        deleteRow,
        updateRow,
        editingRow,
        editingRowId,
        openEditDetail,
        closeEditDetail,
        validationMessage,
        canSubmit,
        persistRows,
        INDEFINITE_DATE,
        toDayjs,
        timelineSegments,
        formatTimelineDate,
        handleBoundaryDateChange,
        data,
        openTree,
        setOpenTree,
        positionTreeItems,
        positionRowId,
        selectedPosition,
        openPositionTree,
        closePositionTree,
        handlePositionSelectionChange,
        confirmPositionSelection,
    };
};
